/**
 * CatalogoJerarquico
 * Componente para visualizar la estructura jerárquica de Áreas, Partidas y Recursos
 * Permite crear, editar y eliminar elementos en cada nivel
 */

import React, { useState, useEffect } from 'react';
import { areaService, partidaService, recursoService } from '../services/catalogoService';
import { toast } from 'react-toastify';
import { FiChevronDown, FiChevronRight, FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import PartidaModal from './PartidaModal';
import RecursoModal from './RecursoModal';
import './CatalogoJerarquico.css';

import Loading from './Loading';
const CatalogoJerarquico = ({ cuentaId, presupuestoId, tipo = 'venta' }) => {
  const [areas, setAreas] = useState([]);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [expandedPartidas, setExpandedPartidas] = useState({});
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [newAreaName, setNewAreaName] = useState('');

  // Partida Modal State
  const [partidaModalOpen, setPartidaModalOpen] = useState(false);
  const [partidaModalMode, setPartidaModalMode] = useState('create');
  const [selectedPartida, setSelectedPartida] = useState(null);
  const [selectedAreaForPartida, setSelectedAreaForPartida] = useState(null);

  // Recurso Modal State
  const [recursoModalOpen, setRecursoModalOpen] = useState(false);
  const [recursoModalMode, setRecursoModalMode] = useState('create');
  const [selectedRecurso, setSelectedRecurso] = useState(null);
  const [selectedPartidaForRecurso, setSelectedPartidaForRecurso] = useState(null);

  // Cargar áreas al montar el componente
  useEffect(() => {
    cargarAreas();
  }, [cuentaId]);

  const cargarAreas = async () => {
    if (!cuentaId) return;

    setLoadingAreas(true);
    try {
      const response = await areaService.getAll({
        cuenta: cuentaId,
        status: 'activo'
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAreas(data);
    } catch (error) {
      console.error('Error al cargar áreas:', error);
      toast.error('Error al cargar áreas');
    } finally {
      setLoadingAreas(false);
    }
  };

  const toggleAreaExpanded = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  const togglePartidaExpanded = (partidaId) => {
    setExpandedPartidas(prev => ({
      ...prev,
      [partidaId]: !prev[partidaId]
    }));
  };

  const crearArea = async () => {
    if (!newAreaName.trim()) {
      toast.error('Ingresa un nombre para el área');
      return;
    }

    try {
      await areaService.create({
        cuenta: cuentaId,
        nombre: newAreaName,
        status: 'activo'
      });
      setNewAreaName('');
      toast.success('Área creada exitosamente');
      cargarAreas();
    } catch (error) {
      console.error('Error al crear área:', error);
      toast.error('Error al crear área');
    }
  };

  const editarArea = async (areaId, nuevoNombre) => {
    if (!nuevoNombre.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }

    try {
      await areaService.update(areaId, { nombre: nuevoNombre });
      setEditingArea(null);
      toast.success('Área actualizada');
      cargarAreas();
    } catch (error) {
      console.error('Error al editar área:', error);
      toast.error('Error al editar área');
    }
  };

  const eliminarArea = async (areaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta área?')) {
      return;
    }

    try {
      await areaService.delete(areaId);
      toast.success('Área eliminada');
      cargarAreas();
    } catch (error) {
      console.error('Error al eliminar área:', error);
      toast.error('Error al eliminar área');
    }
  };

  // Partida handlers
  const abrirPartidaModal = (mode, partida = null, areaId = null) => {
    setPartidaModalMode(mode);
    setSelectedPartida(partida);
    setSelectedAreaForPartida(areaId);
    setPartidaModalOpen(true);
  };

  const cerrarPartidaModal = () => {
    setPartidaModalOpen(false);
    setSelectedPartida(null);
    setSelectedAreaForPartida(null);
  };

  const onPartidaSuccess = () => {
    cargarAreas();
  };

  const eliminarPartida = async (partidaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta partida?')) {
      return;
    }

    try {
      await partidaService.delete(partidaId);
      toast.success('Partida eliminada');
      cargarAreas();
    } catch (error) {
      console.error('Error al eliminar partida:', error);
      toast.error('Error al eliminar partida');
    }
  };

  // Recurso handlers
  const abrirRecursoModal = (mode, recurso = null, partidaId = null) => {
    setRecursoModalMode(mode);
    setSelectedRecurso(recurso);
    setSelectedPartidaForRecurso(partidaId);
    setRecursoModalOpen(true);
  };

  const cerrarRecursoModal = () => {
    setRecursoModalOpen(false);
    setSelectedRecurso(null);
    setSelectedPartidaForRecurso(null);
  };

  const onRecursoSuccess = () => {
    cargarAreas();
  };

  const eliminarRecurso = async (recursoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este recurso?')) {
      return;
    }

    try {
      await recursoService.delete(recursoId);
      toast.success('Recurso eliminado');
      cargarAreas();
    } catch (error) {
      console.error('Error al eliminar recurso:', error);
      toast.error('Error al eliminar recurso');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  if (loadingAreas) {
    return <Loading fullScreen={false} size="sm" mensaje="Cargando catálogo" />;
  }

  return (
    <div className="catalogo-jerarquico">
      <div className="catalogo-header">
        <h3>Catálogo Jerárquico</h3>
        <div className="crear-area-input">
          <input
            type="text"
            placeholder="Nueva área"
            value={newAreaName}
            onChange={(e) => setNewAreaName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && crearArea()}
          />
          <button onClick={crearArea} className="btn-crear">
            <FiPlus /> Agregar Área
          </button>
        </div>
      </div>

      <div className="areas-list">
        {areas.length === 0 ? (
          <div className="empty-state">
            <p>No hay áreas disponibles</p>
          </div>
        ) : (
          areas.map(area => (
            <div key={area.id} className="area-item">
              <div className="area-header">
                <button
                  className="toggle-btn"
                  onClick={() => toggleAreaExpanded(area.id)}
                >
                  {expandedAreas[area.id] ? <FiChevronDown /> : <FiChevronRight />}
                </button>

                {editingArea === area.id ? (
                  <div className="editing-input">
                    <input
                      type="text"
                      defaultValue={area.nombre}
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          editarArea(area.id, e.target.value);
                        }
                      }}
                      onBlur={(e) => editarArea(area.id, e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="area-name">
                    <strong>{area.nombre}</strong>
                    <span className="area-totales">
                      Venta: {formatCurrency(area.valor_unitario_venta)} |
                      Costo: {formatCurrency(area.valor_unitario_costo)} |
                      Margen: {(parseFloat(area.markup_promedio) || 0).toFixed(2)}%
                    </span>
                  </div>
                )}

                <div className="area-actions">
                  {editingArea === area.id ? (
                    <>
                      <button
                        onClick={() => setEditingArea(null)}
                        className="btn-action cancel"
                        title="Cancelar"
                      >
                        <FiX />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditingArea(area.id)}
                        className="btn-action edit"
                        title="Editar"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => eliminarArea(area.id)}
                        className="btn-action delete"
                        title="Eliminar"
                      >
                        <FiTrash2 />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {expandedAreas[area.id] && (
                <div className="area-content">
                  {area.partidas && area.partidas.length > 0 ? (
                    <div className="partidas-list">
                      {area.partidas.map(partida => (
                        <div key={partida.id} className="partida-item">
                          <div className="partida-header">
                            <button
                              className="toggle-btn"
                              onClick={() => togglePartidaExpanded(partida.id)}
                            >
                              {expandedPartidas[partida.id] ? <FiChevronDown /> : <FiChevronRight />}
                            </button>

                            <div className="partida-name">
                              <strong>{partida.nombre}</strong>
                              <span className="partida-totales">
                                Venta: {formatCurrency(partida.valor_unitario_venta)} |
                                Costo: {formatCurrency(partida.valor_unitario_costo)} |
                                Margen: {(parseFloat(partida.markup_promedio) || 0).toFixed(2)}%
                              </span>
                            </div>

                            <div className="partida-actions">
                              <button
                                onClick={() => abrirPartidaModal('edit', partida)}
                                className="btn-action edit"
                                title="Editar"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                onClick={() => eliminarPartida(partida.id)}
                                className="btn-action delete"
                                title="Eliminar"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>

                          {expandedPartidas[partida.id] && (
                            <div className="partida-content">
                              {partida.recursos && partida.recursos.length > 0 ? (
                                <div className="recursos-list">
                                  {partida.recursos.map(recurso => (
                                    <div key={recurso.id} className="recurso-item">
                                      <div className="recurso-details">
                                        <strong>{recurso.nombre}</strong>
                                        <div className="recurso-valores">
                                          <span>Cant: {recurso.cantidad_presupuesto} {recurso.unidad}</span>
                                          <span>Costo: {formatCurrency(recurso.precio_unitario_costo)}</span>
                                          <span>Venta: {formatCurrency(recurso.precio_unitario_venta)}</span>
                                          <span>Margen: {(parseFloat(recurso.markup) || 0).toFixed(2)}%</span>
                                          <span className="subtotal">
                                            Subtotal: {formatCurrency(recurso.subtotal)}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="recurso-actions">
                                        <button
                                          onClick={() => abrirRecursoModal('edit', recurso)}
                                          className="btn-action edit"
                                          title="Editar"
                                        >
                                          <FiEdit2 />
                                        </button>
                                        <button
                                          onClick={() => eliminarRecurso(recurso.id)}
                                          className="btn-action delete"
                                          title="Eliminar"
                                        >
                                          <FiTrash2 />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="empty-recursos">
                                  <p>Sin recursos</p>
                                </div>
                              )}
                              <button
                                onClick={() => abrirRecursoModal('create', null, partida.id)}
                                className="btn-agregar-recurso"
                              >
                                <FiPlus /> Agregar Recurso
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-partidas">
                      <p>Sin partidas</p>
                    </div>
                  )}
                  <button
                    onClick={() => abrirPartidaModal('create', null, area.id)}
                    className="btn-agregar-partida"
                  >
                    <FiPlus /> Agregar Partida
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <PartidaModal
        isOpen={partidaModalOpen}
        mode={partidaModalMode}
        partida={selectedPartida}
        areaId={selectedAreaForPartida}
        onClose={cerrarPartidaModal}
        onSuccess={onPartidaSuccess}
      />

      <RecursoModal
        isOpen={recursoModalOpen}
        mode={recursoModalMode}
        recurso={selectedRecurso}
        partidaId={selectedPartidaForRecurso}
        onClose={cerrarRecursoModal}
        onSuccess={onRecursoSuccess}
      />
    </div>
  );
};

export default CatalogoJerarquico;
